'use client'

import { useEffect, useState } from 'react'

import { useRouter } from 'next/navigation'

import Typography from '@mui/material/Typography'

import classnames from 'classnames'
import { signIn, useSession } from 'next-auth/react'

import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material'

import axios from 'axios'

import themeConfig from '@configs/themeConfig'

import CustomButton from '@/components/layout/shared/CustomButton'
import LogoLogin from '@/components/layout/shared/LogoLogin'
import { useImageVariant } from '@core/hooks/useImageVariant'
import { useSettings } from '@core/hooks/useSettings'

import getUrlApiLogin from '@/utils/getUrlApiLogin'
import showToastAlert from '@components/Toast'

const Login = () => {
  const darkImg = '/images/pages/auth-v2-mask-1-dark.png'
  const lightImg = '/images/pages/auth-v2-mask-1-light.png'

  const { settings } = useSettings()

  const authBackground = useImageVariant('light', lightImg, darkImg)

  const router = useRouter()
  const { data: session, status } = useSession()

  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [loading, setLoading] = useState(false)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  useEffect(() => {
    if (status === 'authenticated') {
      const redirectTo = '/home'

      router.push(redirectTo)
    }
  }, [status, session, router])

  const clearAllCookies = async () => {
    // Hapus client-side cookies
    const cookies = document.cookie.split('; ')

    cookies.forEach(cookie => {
      const [name] = cookie.split('=')

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    })

    console.log('Client-side cookies cleared!')

    // Hapus server-side cookies
    try {
      await axios.get('/api/clear-cookies')
      console.log('Server-side cookies cleared!')
    } catch (error) {
      console.error('Failed to clear server-side cookies:', error)
    }
  }
  const doLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!username) {
      showToastAlert('info', 'Username tidak boleh kosong')

      return
    }

    if (!password) {
      showToastAlert('info', 'Password tidak boleh kosong')

      return
    }
    const url = await getUrlApiLogin()
    let isurlpublic = ''

    if (url === (process.env.NEXT_PUBLIC_API_URL as string)) {
      isurlpublic = 'true'
    } else {
      isurlpublic = 'false'
    }
    setLoading(true)
    await clearAllCookies()
    try {
      const result = await signIn('credentials', {
        redirect: false,
        username: username,
        pass: password,
        isurlpublic: isurlpublic
      })
      if (result?.error) {
        showToastAlert('info', result.error)
      } else {
        // setTimeout(() => {
        router.push(themeConfig.homePageUrl)
        // }, 1000)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className='flex bs-full justify-center'>
        <div
          className={classnames(
            'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
            {
              'border-ie': settings.skin === 'bordered'
            }
          )}
        >
          <div className='pli-6 max-lg:mbs-40 lg:mbe-24 transition duration-150 ease-in-out hover:scale-110'>
            <img
              src='/images/icon/login.png'
              alt='logo'
              width={600}
              height={600}
              className='md:block m-auto max-md:w-[75px] max-md:h-[75px]'
            />
          </div>
          <img src='/images/icon/bg_all.webp' alt='' className='absolute inset-0 w-full h-full object-cover z-[-1]' />
          <img src={authBackground} className='absolute bottom-[4%] z-[-1] is-full max-md:hidden' />
        </div>
        <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
          <div className='absolute block-start-5 sm:block-start-[10px] inline-start-6 sm:inline-start-[10px]'>
            <LogoLogin />
          </div>
          <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
            <div>
              <Typography variant='h5' className='text-center'>
                Selamat datang di
                <strong>{` ${themeConfig.templateName}! 👋🏻`}</strong>
              </Typography>
            </div>
            <form noValidate onSubmit={doLogin} autoComplete='off' className='flex flex-col gap-5'>
              <TextField autoFocus fullWidth label='Username' onChange={e => setUsername(e.target.value)} />
              <TextField
                fullWidth
                label='Password'
                type={isPasswordShown ? 'text' : 'password'}
                onChange={e => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        edge='end'
                        onClick={handleClickShowPassword}
                        onMouseDown={e => e.preventDefault()}
                      >
                        <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                <Button
                  type={'submit'}
                  variant='contained'
                  color='primary'
                  fullWidth
                  sx={{
                    borderRadius: '3.5rem',
                    height: '2.7rem',
                    typography: 'body2',
                    padding: '0 1rem'
                  }}
                  disabled={loading}
                  startIcon={loading ? <i className={`ri-loader-4-line ri-spin animate-spin`} /> : null}
                  aria-busy={loading}
                >
                  {loading ? 'Loading' : 'Log In'}
                </Button>
              </Box>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default Login
